import { type ExtendedAppointment } from "@repo/api";
import {
  Icon,
  LightMode,
  Text,
  Tooltip,
  Variables,
  informationCircleIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  FlexDiv,
  localizeDateTimeRange,
  localizeTimeRange,
  useSettings,
} from "@repo/ui";
import { getTimezoneRegionByTimezone } from "@repo/utils";

import { isCancelledStatus, isCompletedStatus } from "../../utils";

export const AppointmentTime = ({
  variant,
  status,
  startTime,
  endTime,
  customerTimezone,
}: {
  variant: "AGENDA" | "MANAGE";

  status: ExtendedAppointment["appointmentStatus"];
  startTime: string;
  endTime?: string;
  customerTimezone?: string[];
}) => {
  const { dateFnsLocale } = useTranslation();
  const { timezone, market } = useSettings();

  const isTimezoneDifferent =
    customerTimezone && timezone && customerTimezone[0] !== timezone;

  const hasMultipleTimezones = customerTimezone && customerTimezone.length > 1;

  const textColor =
    variant === "AGENDA" &&
    (isCancelledStatus(status) || isCompletedStatus(status))
      ? LightMode.colourTextAndIcon3
      : LightMode.colourTextAndIcon1;

  const lineThrough = variant === "AGENDA" && isCancelledStatus(status);

  return (
    <>
      <Text
        textStyle={variant === "AGENDA" ? "Body.S" : undefined}
        lineThrough={lineThrough}
        color={textColor}
      >
        {variant === "AGENDA"
          ? localizeTimeRange(startTime, endTime, dateFnsLocale)
          : localizeDateTimeRange(startTime, endTime, dateFnsLocale)}
      </Text>
      {isTimezoneDifferent && (
        <FlexDiv gap={Variables.space25} alignItems="center">
          <Text
            textStyle={variant === "AGENDA" ? "Body.S" : undefined}
            lineThrough={lineThrough}
            color={textColor}
          >
            {variant === "AGENDA"
              ? localizeTimeRange(startTime, endTime, dateFnsLocale, timezone)
              : localizeDateTimeRange(
                  startTime,
                  endTime,
                  dateFnsLocale,
                  customerTimezone[0]
                )}
            {` (${getTimezoneRegionByTimezone(market, customerTimezone[0])})`}
          </Text>
          {hasMultipleTimezones && (
            <Tooltip
              tooltipText={t("prompt.multiple_timezone_info")}
              describedById="appointment-time-multiple-timezone-info"
            >
              <Icon paths={informationCircleIcon} />
            </Tooltip>
          )}
        </FlexDiv>
      )}
    </>
  );
};
